import { Fragment, useEffect, useRef, useState } from 'react'
import { useIsClient, useMotion } from '@/components/motion/motion'
import { pageEvent, type PageEventDetail } from '@/components/motion/pageEvents'
import { usePagedViewport } from '@/components/motion/viewport'
import { cx } from '@/lib/cx'

/* "With United, your firm does the buying." On screens that page one section at a time, the whole
   screen fades to night as the section comes into view, and the reasoning lights up word by word,
   once. Smaller screens show it as a navy panel with the text lit. */

const reasons = [
  'Technology is changing the economics of accounting. Routine work is becoming faster and cheaper to produce, and clients will expect more from the accountant they trust.',
  'At the same time, the profession is consolidating, and a generation of owners is retiring and bringing their client books to market.',
].map((text) => text.split(' '))

const wordCount = reasons.reduce((total, words) => total + words.length, 0)
const wordOffsets = reasons.map((_, index) => reasons.slice(0, index).reduce((total, words) => total + words.length, 0))

/** Share of the viewport, or of the section if it is shorter, that the section covers. */
const coverage = (rect: DOMRect, viewportHeight: number, relativeTo: number) =>
  Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)) / Math.max(1, relativeTo)

export const ShiftSection = () => {
  const { paused, reduceMotion } = useMotion()
  const isClient = useIsClient()
  const paged = usePagedViewport()
  const sectionRef = useRef<HTMLElement>(null)
  const nightRef = useRef<HTMLDivElement>(null)
  const isPaused = useRef(paused)
  const [litWords, setLitWords] = useState(0)

  useEffect(() => {
    isPaused.current = paused
  }, [paused])

  // The night: fade the fixed navy layer with how much of the screen the section fills.
  useEffect(() => {
    const section = sectionRef.current
    const night = nightRef.current
    if (!section || !night || !paged) return

    let frame = 0
    const update = () => {
      frame = 0
      const viewportHeight = window.innerHeight || 800
      const covered = coverage(section.getBoundingClientRect(), viewportHeight, viewportHeight)
      night.style.opacity = String(Math.min(1, Math.max(0, (covered - 0.2) / 0.55)))
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }

    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    schedule()
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      night.style.opacity = ''
    }
  }, [paged])

  // The reveal: words light up in sequence once half the section is on screen, or as section
  // paging starts moving to it.
  useEffect(() => {
    const section = sectionRef.current
    if (!section || !paged) return

    let revealed = false
    let lit = 0
    let timer = 0
    let frame = 0

    const play = () => {
      if (revealed) return
      revealed = true
      const interval = Math.max(14, Math.min(40, 1100 / wordCount))
      const step = () => {
        lit = isPaused.current ? wordCount : lit + 1
        setLitWords(lit)
        if (lit < wordCount) timer = window.setTimeout(step, interval)
      }
      timer = window.setTimeout(step, 0)
    }
    const check = () => {
      frame = 0
      const viewportHeight = window.innerHeight || 800
      const rect = section.getBoundingClientRect()
      if (coverage(rect, viewportHeight, Math.min(rect.height, viewportHeight)) >= 0.5) play()
    }
    const schedule = () => {
      if (!frame && !revealed) frame = requestAnimationFrame(check)
    }
    const onPage = (event: Event) => {
      const { target } = (event as CustomEvent<PageEventDetail>).detail
      const top = section.getBoundingClientRect().top + window.scrollY
      if (Math.abs(target - top) < 40) play()
    }

    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    window.addEventListener(pageEvent, onPage)
    schedule()
    return () => {
      window.clearTimeout(timer)
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      window.removeEventListener(pageEvent, onPage)
    }
  }, [paged])

  // Before the page is interactive, on smaller screens, and whenever motion is off, the text is simply shown.
  const night = isClient && paged
  const allLit = !night || paused || reduceMotion

  return (
    <>
      <div ref={nightRef} className="x-night-bg" aria-hidden="true" />
      <section ref={sectionRef} className={cx('x-shift', night && 'has-night')} aria-labelledby="shift-title">
        <div className="x-wrap x-shift-grid">
          <h2 id="shift-title" className="x-shift-title">
            <span className="x-line">With United,</span> <span className="x-line">your firm</span>{' '}
            <span className="x-line x-buying">does the buying.</span>
          </h2>
          <div className="x-shift-text">
            {reasons.map((words, paragraph) => (
              <p key={paragraph}>
                {words.map((word, index) => (
                  <Fragment key={index}>
                    {index > 0 && ' '}
                    <span className={cx('w', (allLit || wordOffsets[paragraph] + index < litWords) && 'on')}>{word}</span>
                  </Fragment>
                ))}
              </p>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
